import { ArrowRight, Check, ChevronDown, ChevronRight, Gift, Loader2, Search, X } from "lucide-react";
import { PiHandbagSimple } from "react-icons/pi";
import { AnimatePresence, motion, useAnimationControls } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import { GTMEvent } from "~/components/gtmProvider/GTMContext";
import useGTM from "~/components/gtmProvider/useGTM";
import BookableItemTags, { BookableItemTag } from "~/components/new/BookableItemTags";
import CartDrawer from "~/components/new/CartDrawer";
import KlarnaBadge from "~/components/new/KlarnaBadge";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import TopProgressBar from "~/components/new/TopProgressBar";
import TreatmentPlannerCard, { TREATMENT_PLANNER_IMAGE_URL } from "~/components/new/TreatmentPlannerCard";
import { Badge } from "~/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "~/components/ui/sheet";
import { CartItem, SelectedAddOn, useBookingFlowStore } from "~/stores/booking-flow-store";
import { Language, useLanguageStore } from "~/stores/language-store";
import { KLARNA_MIN_CART_TOTAL_EUR } from "~/lib/klarna";
import { BookingFunnelBookableItemType } from "~/types/server-types/BookingFunnelBookableItemType";

// Types for CMS data
type BookableItemImage = {
    id: string;
    url: string;
    altText: string | null;
    order: number;
};

type VariantOption = {
    id: string;
    optionLabelDe: string;
    optionLabelEn: string;
    order: number;
    treatmentVariant: {
        id: string;
        name: string;
        priceCents: number;
        steps: { minuteEnd: number }[];
    };
};

type AddOn = {
    id: string;
    nameDe: string;
    nameEn: string;
    descriptionDe: string;
    descriptionEn: string;
    imageUrl: string | null;
    order: number;
    treatmentVariant: {
        id: string;
        name: string;
        priceCents: number;
        steps: { minuteEnd: number }[];
    };
};

type BundleData = {
    id: string;
    name: string;
    unitPriceGross: number;
    unitQuantity: number;
    treatmentVariantId: string;
    treatmentVariant: {
        id: string;
        name: string;
        priceCents: number;
        steps: { minuteEnd: number }[];
    };
};

// Preselect the bundle with the fewest units (lowest amount of treatments)
const pickDefaultUpsellBundle = (bundles: BundleData[]): BundleData | null =>
    bundles.length === 0
        ? null
        : bundles.reduce((min, b) => (b.unitQuantity < min.unitQuantity ? b : min), bundles[0]);

// Per-session bundle prices are floored so a price like 299,66 never displays as a rounded-up 300
const formatBundleUnitPrice = (unitPriceGross: number): string =>
    new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.floor(Number(unitPriceGross)));

type BookableItem = {
    id: string;
    nameDe: string;
    nameEn: string;
    descriptionDe: string;
    descriptionEn: string;
    type: BookingFunnelBookableItemType;
    keyBenefitsDe: string[];
    keyBenefitsEn: string[];
    youShouldKnowThisDe: string;
    youShouldKnowThisEn: string;
    order: number;
    images: BookableItemImage[];
    variantOptions: VariantOption[];
    addOns: AddOn[];
    tags: BookableItemTag[];
    bundle?: BundleData | null;           // For BUNDLE type items
    availableBundles: BundleData[];       // For SERVICE type items - bundles available for upsell
};

type BookableItemWithCategories = BookableItem & {
    categoryNameDe: string | null;
    categoryNameEn: string | null;
    symptomCategoryNameDe: string | null;
    symptomCategoryNameEn: string | null;
};

type CMSCategoryChild = {
    id: string;
    nameDe: string;
    nameEn: string;
    imageUrl: string | null;
    order: number;
    bookableItems: BookableItem[];
};

type CMSCategory = {
    id: string;
    nameDe: string;
    nameEn: string;
    imageUrl: string | null;
    order: number;
    children: CMSCategoryChild[];
    bookableItems: BookableItem[];
};

// Bottom bar with cart summary and continue button - only shown when cart has items
const FixedBottomBar = () => {
    const [isCartOpen, setIsCartOpen] = useState(false);
    const controls = useAnimationControls();

    const { cartItems, selectedStore } = useBookingFlowStore(
        useShallow((state) => ({
            cartItems: state.cartItems,
            selectedStore: state.selectedStore,
        }))
    );

    const { language } = useLanguageStore();
    const navigate = useNavigate();

    const itemCount = cartItems.length;
    // Start at 0 so a fresh mount (e.g. navigating back to a screen with items
    // already in the cart) also triggers the "blop" animation.
    const prevCountRef = useRef(0);

    // "Blop" the treatments icon whenever an item is added, mirroring CartIconButton.
    useEffect(() => {
        if (itemCount > prevCountRef.current) {
            controls.start({
                scale: [1, 1.35, 0.95, 1],
                transition: { duration: 0.4, ease: "easeInOut" },
            });
        }
        prevCountRef.current = itemCount;
    }, [itemCount, controls]);

    // CMS Config
    const { data: cmsData } = trpc.publicBookingFunnel.getServiceSelectionConfig.useQuery({
        storeId: selectedStore?.id ?? -1,
    }, {
        enabled: !!selectedStore,
        staleTime: 60000,
    });
    const config = cmsData?.config;

    // The bar stays mounted while the cart is empty, so the drawer state must be
    // reset here or it pops open again on the next add
    if (itemCount === 0 && isCartOpen) {
        setIsCartOpen(false);
    }

    // Don't render if cart is empty
    if (cartItems.length === 0) {
        return null;
    }

    const handleClick = () => {
        navigate("/find-time");
    };

    const continueText = language === Language.GERMAN
        ? (config?.continueButtonTextDe ?? 'WEITER')
        : (config?.continueButtonTextEn ?? 'CONTINUE');

    const treatmentsText = cartItems.length === 1
        ? (language === Language.GERMAN ? 'Behandlung' : 'Treatment')
        : (language === Language.GERMAN
            ? (config?.treatmentsLabelDe ?? 'Behandlungen')
            : (config?.treatmentsLabelEn ?? 'Treatments'));

    return (
        <>
            <div className="fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-3 px-6 pt-4 pb-6 bg-light-burgundy">
                <PrimaryActionButton
                    onClick={handleClick}
                    label={continueText.toUpperCase()}
                    rightIcon={<ArrowRight />}
                />
                <div className="w-full flex flex-row justify-center items-center gap-2">
                    <button
                        type="button"
                        className="flex items-center justify-center gap-1.5 text-center text-burgundy cursor-pointer text-sm font-light"
                        onClick={() => setIsCartOpen(true)}>
                        <motion.span className="relative inline-flex" animate={controls}>
                            <PiHandbagSimple className="w-5 h-5" />
                            {cartItems.length > 0 && (
                                <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-0.5 flex items-center justify-center bg-burgundy text-white text-[10px] font-medium rounded-full">
                                    {cartItems.length}
                                </span>
                            )}
                        </motion.span>
                        <span className="underline ml-2">{cartItems.length} {treatmentsText}</span>
                    </button>
                </div>
            </div>

            <CartDrawer
                open={isCartOpen}
                onClose={() => setIsCartOpen(false)}
            />
        </>
    );
};

// Bookable Item Card
type BookableItemCardProps = {
    item: BookableItem;
    onSelect: () => void;
    onMoreInfo?: () => void;
    isInCart: boolean;
    isRecommended?: boolean;
    showMoreInfoLink?: boolean;
    moreInfoLinkText?: string;
};

const BookableItemCard = ({ item, onSelect, onMoreInfo, isInCart, isRecommended = false, showMoreInfoLink = false, moreInfoLinkText = 'Mehr dazu' }: BookableItemCardProps) => {
    const { language } = useLanguageStore();
    const name = language === Language.GERMAN ? item.nameDe : item.nameEn;
    const description = language === Language.GERMAN ? item.descriptionDe : item.descriptionEn;

    // Check if this is a bundle type item
    const isBundle = item.type === BookingFunnelBookableItemType.BUNDLE && item.bundle;

    // Get price and duration from first variant option (for SERVICE) or from bundle (for BUNDLE)
    const firstVariant = item.variantOptions[0]?.treatmentVariant;
    const bundleVariant = item.bundle?.treatmentVariant;

    // For bundles, use bundle pricing; for services with available bundles, show cheapest per-session bundle price
    const cheapestBundlePrice = !isBundle && item.availableBundles.length > 0
        ? Math.min(...item.availableBundles.map(b => Number(b.unitPriceGross)))
        : null;
    const price = isBundle && item.bundle
        ? Number(item.bundle.unitPriceGross) * item.bundle.unitQuantity
        : cheapestBundlePrice ?? (firstVariant ? firstVariant.priceCents / 100 : 0);
    const duration = isBundle && bundleVariant
        ? bundleVariant.steps?.[0]?.minuteEnd ?? 0
        : (firstVariant?.steps?.[0]?.minuteEnd ?? 0);
    const hasMultipleVariants = !isBundle && item.variantOptions.length > 1;
    const hasImage = item.images.length > 0;
    const hasBundleUpsell = !isBundle && item.availableBundles.length > 0;
    const singleTreatmentPrice = ((isBundle ? bundleVariant : firstVariant)?.priceCents ?? 0) / 100;
    const bundleTotalPrice = isBundle && item.bundle
        ? Number(item.bundle.unitPriceGross) * item.bundle.unitQuantity
        : 0;
    const maxUpsellBundleTotalPrice = hasBundleUpsell
        ? Math.max(...item.availableBundles.map(b => Number(b.unitPriceGross) * b.unitQuantity))
        : 0;
    const showKlarnaBadge = Math.max(singleTreatmentPrice, bundleTotalPrice, maxUpsellBundleTotalPrice) >= KLARNA_MIN_CART_TOTAL_EUR;

    // For bundles, calculate the regular price for strikethrough display
    const regularPrice = isBundle && bundleVariant
        ? (bundleVariant.priceCents / 100) * (item.bundle?.unitQuantity ?? 1)
        : 0;

    const formattedPrice = cheapestBundlePrice !== null
        ? formatBundleUnitPrice(cheapestBundlePrice)
        : new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);

    return (
        <div
            className={`relative flex flex-col cursor-pointer transition-all rounded-[8px] border drop-shadow-md overflow-hidden ${isRecommended
                ? 'min-w-0 p-3 min-[401px]:p-4 bg-linear-to-b to-white from-[#FBEEF0]'
                : 'p-4 bg-white'
                } ${isInCart
                    ? 'border-burgundy border-2'
                    : 'border-burgundy/20 hover:border-burgundy/40'
                }`}
            onClick={onSelect}
        >
            {/* In Cart Indicator */}
            {isInCart && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-burgundy rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                </div>
            )}
            {/* {hasImage && (
                <div className="w-full h-20 mb-2 rounded overflow-hidden">
                    <img
                        src={item.images[0].url}
                        alt={name}
                        className="w-full h-full object-cover"
                    />
                </div>
            )} */}
            <div className="w-full flex flex-row justify-between gap-2 items-start">
                <span className="font-medium text-burgundy whitespace-pre-wrap wrap-anywhere line-clamp-3 text-sm">
                    {name}
                </span>
                {/* Tags give way to the in-cart indicator, which occupies the same corner */}
                {!isInCart && !isRecommended && <BookableItemTags tags={item.tags} />}
            </div>

            {/* Description for non-recommended items - truncated to 1 line */}
            {!isRecommended && description && (
                <p className="text-xs text-burgundy/60 line-clamp-1 mt-1">
                    {description}
                </p>
            )}

            <div className="flex flex-row justify-between items-end mt-auto pt-2">
                {isBundle && item.bundle ? (
                    // Bundle pricing display: duration | "5x ab 99€" with strikethrough of regular price
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs text-burgundy">{duration} MIN</span>
                        </div>
                        <div className='flex flex-row items-center gap-1'>
                            <Gift className="w-3 h-3 text-burgundy" />
                            <span className="text-xs text-burgundy font-medium">
                                {item.bundle.unitQuantity}x{formatBundleUnitPrice(item.bundle.unitPriceGross)} EUR
                            </span>
                            {showKlarnaBadge && <KlarnaBadge />}
                        </div>
                    </div>
                ) : isRecommended ? (
                    // Stacked layout for recommended items
                    <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-burgundy">{duration} MIN</span>
                        {price > 0 && (
                            <div className="flex items-center gap-1">
                                {hasBundleUpsell && <Gift className="w-3 h-3 text-burgundy" />}
                                <span className="text-xs text-burgundy/50">
                                    {language === Language.GERMAN ? 'ab ' : 'from '}{formattedPrice} EUR
                                </span>
                                {showKlarnaBadge && <KlarnaBadge />}
                            </div>
                        )}
                    </div>
                ) : (
                    // Inline layout for non-recommended: duration | price
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-burgundy">{duration} MIN</span>
                        {price > 0 && (
                            <>
                                <span className="text-xs text-burgundy/30">|</span>
                                {hasBundleUpsell && <Gift className="w-3 h-3 text-burgundy" />}
                                <span className="text-xs text-burgundy/50">
                                    {language === Language.GERMAN ? 'ab ' : 'from '}{formattedPrice} EUR
                                </span>
                                {showKlarnaBadge && <KlarnaBadge />}
                            </>
                        )}
                    </div>
                )}
                {showMoreInfoLink && (
                    <span
                        className="text-xs text-burgundy/60 underline cursor-pointer hover:text-burgundy transition-colors"
                        onClick={(e) => { e.stopPropagation(); onMoreInfo?.(); }}
                    >
                        {moreInfoLinkText}
                    </span>
                )}
            </div>
        </div>
    );
};

// Category Card
type CategoryCardProps = {
    category: CMSCategory;
    onClick: () => void;
    hasChildren: boolean;
};

const CategoryCard = ({ category, onClick, hasChildren }: CategoryCardProps) => {
    const { language } = useLanguageStore();
    const name = language === Language.GERMAN ? category.nameDe : category.nameEn;

    return (
        <div
            className="relative h-[90px] flex flex-col justify-end p-4 rounded-[5px] cursor-pointer transition-all duration-200 drop-shadow-md overflow-hidden border border-burgundy/20 hover:border-burgundy/40"
            onClick={onClick}
        >
            {category.imageUrl ? (
                <>
                    {/* Full image background */}
                    <img
                        src={category.imageUrl}
                        alt={name}
                        className="absolute top-0 left-0 w-full h-full object-cover"
                    />
                    {/* Dark overlay on image only */}
                    <div className="absolute top-0 left-0 w-full h-full bg-black/20" />
                </>
            ) : (
                <div className="absolute top-0 left-0 w-full h-full bg-burgundy/10" />
            )}
            <div className="flex items-center justify-between relative z-10">
                <span className={`font-medium text-[15px] ${category.imageUrl ? 'text-white' : 'text-burgundy'}`}>{name}</span>
            </div>
        </div>
    );
};

// Accordion Category
type AccordionCategoryProps = {
    category: CMSCategory | CMSCategoryChild;
    onSelectItem: (item: BookableItem) => void;
    onMoreInfo: (item: BookableItem) => void;
    cartItemIds: string[];
    moreInfoLinkText: string;
};

const AccordionCategory = ({ category, onSelectItem, onMoreInfo, cartItemIds, moreInfoLinkText }: AccordionCategoryProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { language } = useLanguageStore();
    const name = language === Language.GERMAN ? category.nameDe : category.nameEn;

    const children = 'children' in category ? category.children : [];
    const hasChildren = children.length > 0;
    const hasItems = category.bookableItems.length > 0;
    const totalItems = hasChildren
        ? children.reduce((sum, child) => sum + child.bookableItems.length, 0)
        : category.bookableItems.length;

    return (
        <div className="border border-burgundy/20 rounded-lg overflow-hidden">
            <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-burgundy/5 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-3">
                    {category.imageUrl && (
                        <img src={category.imageUrl} alt={name} className="w-10 h-10 object-cover rounded" />
                    )}
                    <div>
                        <span className="font-medium text-burgundy">{name}</span>
                        <span className="text-xs text-burgundy/50 ml-2">({totalItems})</span>
                    </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-burgundy/50 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </div>

            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="p-4 pt-0 space-y-4">
                            {/* Direct bookable items */}
                            {hasItems && (
                                <div className="grid grid-cols-1 gap-3">
                                    {category.bookableItems.map((item) => (
                                        <BookableItemCard
                                            key={item.id}
                                            item={item}
                                            onSelect={() => onSelectItem(item)}
                                            onMoreInfo={() => onMoreInfo(item)}
                                            isInCart={cartItemIds.includes(item.id)}
                                            showMoreInfoLink
                                            moreInfoLinkText={moreInfoLinkText}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Child categories */}
                            {hasChildren && children.map((child) => (
                                <div key={child.id} className="space-y-2">
                                    <h4 className="text-sm font-medium text-burgundy/70 px-1">
                                        {language === Language.GERMAN ? child.nameDe : child.nameEn}
                                    </h4>
                                    <div className="grid grid-cols-1 gap-3">
                                        {child.bookableItems.map((item) => (
                                            <BookableItemCard
                                                key={item.id}
                                                item={item}
                                                onSelect={() => onSelectItem(item)}
                                                onMoreInfo={() => onMoreInfo(item)}
                                                isInCart={cartItemIds.includes(item.id)}
                                                showMoreInfoLink
                                                moreInfoLinkText={moreInfoLinkText}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const ServiceSelection = () => {
    const {
        selectedStore, selectedPath, cartItems, setCartItems, deeplinkItem, setDeeplinkItem,
        deeplinkCategoryId, setDeeplinkCategoryId,
    } = useBookingFlowStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            selectedPath: state.selectedPath,
            cartItems: state.cartItems,
            setCartItems: state.setCartItems,
            deeplinkItem: state.deeplinkItem,
            setDeeplinkItem: state.setDeeplinkItem,
            deeplinkCategoryId: state.deeplinkCategoryId,
            setDeeplinkCategoryId: state.setDeeplinkCategoryId,
        }))
    );
    const { sendEvent } = useGTM();
    const navigate = useNavigate();
    const { t, language } = useLanguageStore();
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [selectedItem, setSelectedItem] = useState<BookableItem | null>(null);
    const [isInfoDrawerOpen, setIsInfoDrawerOpen] = useState(false);
    const [isAddOnDrawerOpen, setIsAddOnDrawerOpen] = useState(false);
    const [isBundleUpsellDrawerOpen, setIsBundleUpsellDrawerOpen] = useState(false);
    const [availableBundles, setAvailableBundles] = useState<BundleData[]>([]);
    const [selectedUpsellBundle, setSelectedUpsellBundle] = useState<BundleData | null>(null);
    const [pendingBundleAction, setPendingBundleAction] = useState<{ type: 'purchase' | 'redemption'; bundle: BundleData } | null>(null);
    const [selectedAddOns, setSelectedAddOns] = useState<AddOn[]>([]);
    const [selectedVariantOption, setSelectedVariantOption] = useState<VariantOption | null>(null);
    const [infoDrawerMode, setInfoDrawerMode] = useState<'info' | 'options'>('info');

    useEffect(() => {
        window.scroll(0, 0);
        sendEvent(GTMEvent.BOOKING_FUNNEL_SERVICE_SELECTION, {
            branch: {
                id: selectedStore?.id,
                name: selectedStore?.name,
            },
            user: {
                id: null,
                has_account: false,
                email: null,
                phone: null,
            },
        });

        // Preload treatment planner image
        const img = new Image();
        img.src = TREATMENT_PLANNER_IMAGE_URL;
    }, []);

    // Reset scroll position when navigating between category grid and treatment list
    useEffect(() => {
        scrollContainerRef.current?.scrollTo({ top: 0 });
    }, [selectedCategoryId]);

    useEffect(() => {
        if (selectedCategoryId) {
            window.scrollTo(0, 0);
        }
    }, [selectedCategoryId]);

    useEffect(() => {
        if (!selectedStore) {
            navigate("/");
        }
    }, [selectedStore]);

    // When arriving via a deeplink handoff, auto-open the treatment's info popup
    // over the normal treatment list, then consume the flag so it only opens once.
    useEffect(() => {
        if (!deeplinkItem) return;
        setSelectedItem(deeplinkItem as unknown as BookableItem);
        setSelectedAddOns([]);
        setSelectedVariantOption(null);
        setInfoDrawerMode('info');
        setIsInfoDrawerOpen(true);
        setDeeplinkItem(null);
    }, [deeplinkItem]);

    // When arriving via a category deeplink handoff, open that category directly
    // (no popup), then consume the flag so back navigation works as usual.
    useEffect(() => {
        if (!deeplinkCategoryId) return;
        setSelectedCategoryId(deeplinkCategoryId);
        setDeeplinkCategoryId(null);
    }, [deeplinkCategoryId]);

    useEffect(() => {
        if (isBundleUpsellDrawerOpen && selectedItem && selectedVariantOption) {
            sendEvent(GTMEvent.BOOKING_FUNNEL_BUNDLE_UPSELL_PRESENTED, {
                bookable_item_id: selectedItem.id,
                bookable_item_name: selectedItem.nameDe,
                oneoff_price_cents: selectedVariantOption.treatmentVariant.priceCents,
                available_bundles: availableBundles.map(b => ({
                    bundle_id: b.id,
                    bundle_name: b.name,
                    bundle_unit_price_gross: b.unitPriceGross,
                    bundle_quantity: b.unitQuantity,
                    bundle_total_price: Number(b.unitPriceGross) * b.unitQuantity,
                })),
            });
        }
    }, [isBundleUpsellDrawerOpen]);

    useEffect(() => {
        if (isAddOnDrawerOpen && selectedItem) {
            sendEvent(GTMEvent.BOOKING_FUNNEL_ADDON_POPUP_PRESENTED, {
                bookable_item_id: selectedItem.id,
                bookable_item_name: selectedItem.nameDe,
                available_addons_count: selectedItem.addOns.length,
            });
        }
    }, [isAddOnDrawerOpen]);

    // Fetch CMS data
    const { data: cmsData, isLoading } = trpc.publicBookingFunnel.getServiceSelectionConfig.useQuery(
        { storeId: selectedStore?.id ?? -1 },
        { enabled: !!selectedStore?.id, staleTime: 60000 } // 1 minute cache
    );

    const config = cmsData?.config;
    const categories = cmsData?.categories || [];
    const symptomCategories = cmsData?.symptomCategories || [];
    const recommendedItems = cmsData?.recommendedItems || [];

    // Get cart item IDs for checking if in cart
    const cartItemIds = cartItems.map((item) => item.bookableItemId);

    // Get current categories based on path
    const currentCategories = selectedPath === 'browse-by-category' ? categories : symptomCategories;

    // Get selected category (can be top-level or child)
    const selectedCategory = useMemo((): (CMSCategory | CMSCategoryChild) | null => {
        if (!selectedCategoryId) return null;
        for (const cat of currentCategories) {
            if (cat.id === selectedCategoryId) return cat;
            for (const child of cat.children) {
                if (child.id === selectedCategoryId) return child;
            }
        }
        return null;
    }, [selectedCategoryId, currentCategories]);

    // Helper to check if category has children (only top-level categories do)
    const categoryHasChildren = (cat: CMSCategory | CMSCategoryChild): cat is CMSCategory => {
        return 'children' in cat && Array.isArray(cat.children);
    };

    // Flatten ALL bookable items from entire system (for global search)
    const allSystemBookableItems = useMemo(() => {
        const itemsMap = new Map<string, BookableItemWithCategories>();

        // From categories - attach category names
        categories.forEach((cat) => {
            cat.bookableItems.forEach(item => {
                const existing = itemsMap.get(item.id);
                itemsMap.set(item.id, {
                    ...item,
                    categoryNameDe: existing?.categoryNameDe ?? cat.nameDe,
                    categoryNameEn: existing?.categoryNameEn ?? cat.nameEn,
                    symptomCategoryNameDe: existing?.symptomCategoryNameDe ?? null,
                    symptomCategoryNameEn: existing?.symptomCategoryNameEn ?? null,
                });
            });
            cat.children.forEach((child) => {
                child.bookableItems.forEach(item => {
                    const existing = itemsMap.get(item.id);
                    itemsMap.set(item.id, {
                        ...item,
                        categoryNameDe: existing?.categoryNameDe ?? child.nameDe,
                        categoryNameEn: existing?.categoryNameEn ?? child.nameEn,
                        symptomCategoryNameDe: existing?.symptomCategoryNameDe ?? null,
                        symptomCategoryNameEn: existing?.symptomCategoryNameEn ?? null,
                    });
                });
            });
        });

        // From symptomCategories - attach symptom category names
        symptomCategories.forEach((cat) => {
            cat.bookableItems.forEach(item => {
                const existing = itemsMap.get(item.id);
                itemsMap.set(item.id, {
                    ...(existing ?? item),
                    categoryNameDe: existing?.categoryNameDe ?? null,
                    categoryNameEn: existing?.categoryNameEn ?? null,
                    symptomCategoryNameDe: existing?.symptomCategoryNameDe ?? cat.nameDe,
                    symptomCategoryNameEn: existing?.symptomCategoryNameEn ?? cat.nameEn,
                });
            });
            cat.children.forEach((child) => {
                child.bookableItems.forEach(item => {
                    const existing = itemsMap.get(item.id);
                    itemsMap.set(item.id, {
                        ...(existing ?? item),
                        categoryNameDe: existing?.categoryNameDe ?? null,
                        categoryNameEn: existing?.categoryNameEn ?? null,
                        symptomCategoryNameDe: existing?.symptomCategoryNameDe ?? child.nameDe,
                        symptomCategoryNameEn: existing?.symptomCategoryNameEn ?? child.nameEn,
                    });
                });
            });
        });

        return Array.from(itemsMap.values());
    }, [categories, symptomCategories]);

    // Filter by search - searches ALL items in system regardless of selected path
    const searchFilteredItems = useMemo(() => {
        if (!searchQuery.trim()) return allSystemBookableItems;
        const query = searchQuery.toLowerCase().trim();
        return allSystemBookableItems.filter((item) => {
            const name = language === Language.GERMAN ? item.nameDe : item.nameEn;
            return name.toLowerCase().trim().includes(query)

            // const categoryName = language === Language.GERMAN
            //     ? item.categoryNameDe
            //     : item.categoryNameEn;
            // const symptomCategoryName = language === Language.GERMAN
            //     ? item.symptomCategoryNameDe
            //     : item.symptomCategoryNameEn;

            // return name.toLowerCase().trim().includes(query) ||
            //     categoryName?.toLowerCase().trim().includes(query) ||
            //     symptomCategoryName?.toLowerCase().trim().includes(query);
        });
    }, [allSystemBookableItems, searchQuery, language]);

    // Handle closing search
    const handleCloseSearch = () => {
        setSearchQuery('');
        setIsSearchExpanded(false);
    };

    // Handle opening search
    const handleOpenSearch = () => {
        setIsSearchExpanded(true);
    };

    // Focus search input when search expands
    useEffect(() => {
        if (isSearchExpanded) {
            // Delay to ensure the input is rendered after AnimatePresence exit/enter transition
            const timer = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 180); // Slightly longer than the exit animation (150ms) + buffer
            return () => clearTimeout(timer);
        }
    }, [isSearchExpanded]);

    const handleBack = () => {
        if (selectedCategoryId) {
            setSelectedCategoryId(null);
        } else {
            navigate("/select-path");
        }
    };

    // Opens info drawer for recommended items or when "Mehr dazu" is clicked
    const handleOpenInfoDrawer = (item: BookableItem) => {
        // Check if item is already in cart - if so, remove it
        const isAlreadyInCart = cartItemIds.includes(item.id);
        if (isAlreadyInCart) {
            removeFromCart(item.id);
            return;
        }

        setSelectedItem(item);
        setSelectedAddOns([]);
        setSelectedVariantOption(null);
        setInfoDrawerMode('info');
        setIsInfoDrawerOpen(true);
    };

    // For non-recommended items: fast path if single variant + no addons
    const handleSelectItem = (item: BookableItem) => {
        // Check if item is already in cart - if so, remove it
        const isAlreadyInCart = cartItemIds.includes(item.id);
        if (isAlreadyInCart) {
            removeFromCart(item.id);
            return;
        }

        setSelectedItem(item);
        setSelectedAddOns([]);

        // For BUNDLE type items, directly add to cart (bundles are self-contained)
        if (item.type === BookingFunnelBookableItemType.BUNDLE && item.bundle) {
            // Create a synthetic variant option for the bundle
            const bundleVariantOption: VariantOption = {
                id: 'bundle-' + item.bundle.id,
                optionLabelDe: item.bundle.name || item.nameDe,
                optionLabelEn: item.bundle.name || item.nameEn,
                order: 0,
                treatmentVariant: item.bundle.treatmentVariant,
            };
            addToCartWithBundle(item, bundleVariantOption, [], item.bundle);
            return;
        }

        const hasMultipleVariants = item.variantOptions.length > 1;

        if (hasMultipleVariants) {
            // Multiple variants - show info drawer with options mode
            setSelectedVariantOption(null);
            setInfoDrawerMode('options');
            setIsInfoDrawerOpen(true);
        } else {
            // Single variant - check for available bundles synchronously (prefetched data)
            if (item.availableBundles && item.availableBundles.length > 0) {
                // Show bundle upsell drawer
                setSelectedVariantOption(item.variantOptions[0]);
                setAvailableBundles(item.availableBundles);
                setSelectedUpsellBundle(pickDefaultUpsellBundle(item.availableBundles));
                setIsBundleUpsellDrawerOpen(true);
            } else if (item.addOns.length > 0) {
                // Single variant with add-ons - show add-on drawer
                setSelectedVariantOption(item.variantOptions[0]);
                setIsAddOnDrawerOpen(true);
            } else {
                // Single variant, no add-ons - instant add to cart
                addToCart(item, item.variantOptions[0], []);
            }
        }
    };

    const handleSelectVariant = (option: VariantOption) => {
        setSelectedVariantOption(option);
        setIsInfoDrawerOpen(false);

        // Filter bundles for this specific variant
        const bundlesForVariant = selectedItem?.availableBundles?.filter(
            b => b.treatmentVariant.id === option.treatmentVariant.id
        ) || [];

        if (bundlesForVariant.length > 0) {
            // Show bundle upsell drawer for this variant
            setAvailableBundles(bundlesForVariant);
            setSelectedUpsellBundle(pickDefaultUpsellBundle(bundlesForVariant));
            setIsBundleUpsellDrawerOpen(true);
        } else if (selectedItem?.addOns && selectedItem.addOns.length > 0) {
            setIsAddOnDrawerOpen(true);
        } else {
            addToCart(selectedItem!, option, []);
        }
    };

    // Called when "AUSWÄHLEN" button is clicked in info drawer
    const handleInfoDrawerSelect = () => {
        if (!selectedItem) return;

        // For BUNDLE type items, directly add to cart with the bundle
        if (selectedItem.type === BookingFunnelBookableItemType.BUNDLE && selectedItem.bundle) {
            const bundleVariantOption: VariantOption = {
                id: 'bundle-' + selectedItem.bundle.id,
                optionLabelDe: selectedItem.bundle.name || selectedItem.nameDe,
                optionLabelEn: selectedItem.bundle.name || selectedItem.nameEn,
                order: 0,
                treatmentVariant: selectedItem.bundle.treatmentVariant,
            };
            addToCartWithBundle(selectedItem, bundleVariantOption, [], selectedItem.bundle);
            setIsInfoDrawerOpen(false);
            return;
        }

        const hasMultipleVariants = selectedItem.variantOptions.length > 1;
        const hasAddOns = selectedItem.addOns.length > 0;

        if (infoDrawerMode === 'info' && hasMultipleVariants) {
            // Switch to options mode to show variant selection
            setInfoDrawerMode('options');
        } else {
            // Single variant - check for available bundles first (upsell opportunity)
            const variant = selectedItem.variantOptions[0];
            setSelectedVariantOption(variant);
            setIsInfoDrawerOpen(false);

            // Check for available bundles for this SERVICE item
            if (selectedItem.availableBundles && selectedItem.availableBundles.length > 0) {
                // Show bundle upsell drawer
                setAvailableBundles(selectedItem.availableBundles);
                setSelectedUpsellBundle(pickDefaultUpsellBundle(selectedItem.availableBundles));
                setIsBundleUpsellDrawerOpen(true);
            } else if (hasAddOns) {
                setIsAddOnDrawerOpen(true);
            } else {
                addToCart(selectedItem, variant, []);
            }
        }
    };

    const toggleAddOn = (addOn: AddOn) => {
        setSelectedAddOns((prev) => {
            const isSelected = prev.some((a) => a.id === addOn.id);
            return isSelected ? prev.filter((a) => a.id !== addOn.id) : [...prev, addOn];
        });
    };

    const confirmAddOns = () => {
        if (!selectedItem || !selectedVariantOption) return;
        sendEvent(GTMEvent.BOOKING_FUNNEL_ADDON_POPUP_CONFIRMED, {
            bookable_item_id: selectedItem.id,
            bookable_item_name: selectedItem.nameDe,
            addons_selected_count: selectedAddOns.length,
            addons_total_value_cents: selectedAddOns.reduce((sum, a) => sum + a.treatmentVariant.priceCents, 0),
            addons_list: selectedAddOns.map(a => ({
                addon_id: a.id,
                addon_name: a.nameDe,
                addon_price_cents: a.treatmentVariant.priceCents,
            })),
        });
        if (pendingBundleAction?.type === 'purchase') {
            addToCartWithBundle(selectedItem, selectedVariantOption, selectedAddOns, pendingBundleAction.bundle);
        } else if (pendingBundleAction?.type === 'redemption') {
            addToCartAsRedemption(selectedItem, selectedVariantOption, selectedAddOns, pendingBundleAction.bundle);
        } else {
            addToCart(selectedItem, selectedVariantOption, selectedAddOns);
        }
        setPendingBundleAction(null);
        setIsAddOnDrawerOpen(false);
    };

    const skipAddOns = () => {
        if (!selectedItem || !selectedVariantOption) return;
        if (pendingBundleAction?.type === 'purchase') {
            addToCartWithBundle(selectedItem, selectedVariantOption, [], pendingBundleAction.bundle);
        } else if (pendingBundleAction?.type === 'redemption') {
            addToCartAsRedemption(selectedItem, selectedVariantOption, [], pendingBundleAction.bundle);
        } else {
            addToCart(selectedItem, selectedVariantOption, []);
        }
        setPendingBundleAction(null);
        setIsAddOnDrawerOpen(false);
    };

    const addToCart = (item: BookableItem, variantOption: VariantOption, addOns: AddOn[]) => {
        const variant = variantOption.treatmentVariant;
        const duration = variant.steps?.[0]?.minuteEnd ?? 0;
        const itemCategoryNameDe = allSystemBookableItems.find(i => i.id === item.id)?.categoryNameDe ?? '';

        const addOnItems: SelectedAddOn[] = addOns.map((addon) => ({
            addOnId: addon.id,
            addOnNameDe: addon.nameDe,
            addOnNameEn: addon.nameEn,
            variantId: addon.treatmentVariant.id,
            variantName: addon.treatmentVariant.name,
            price: addon.treatmentVariant.priceCents / 100,
            durationMinutes: addon.treatmentVariant.steps?.[0]?.minuteEnd ?? 0,
        }));

        const newCartItem: CartItem = {
            bookableItemId: item.id,
            bookableItemNameDe: item.nameDe,
            bookableItemNameEn: item.nameEn,
            bookableItemDescriptionDe: item.descriptionDe,
            bookableItemDescriptionEn: item.descriptionEn,
            bookableItemType: item.type,
            bookableItemImageUrl: item.images[0]?.url,
            selectedVariantId: variant.id,
            selectedVariantName: variant.name,
            selectedVariantOptionLabelDe: variantOption.optionLabelDe,
            selectedVariantOptionLabelEn: variantOption.optionLabelEn,
            price: variant.priceCents / 100,
            durationMinutes: duration,
            addOns: addOnItems,
            categoryNameDe: itemCategoryNameDe,
        };

        setCartItems([...cartItems, newCartItem]);

        sendEvent(GTMEvent.BOOKING_FUNNEL_ADD_TO_CART, {
            items: [{
                product_id: variant.id,
                product_name: variant.name,
                category: itemCategoryNameDe,
                price_net: (variant.priceCents / 100) / 1.19,
                price_gross: variant.priceCents / 100,
                quantity: 1,
            }],
            branch: {
                id: selectedStore?.id,
                name: selectedStore?.name,
            },
            user: { id: null, has_account: false, email: null, phone: null },
        });

        setSelectedItem(null);
        setSelectedVariantOption(null);
        setSelectedAddOns([]);
    };

    const addToCartWithBundle = (item: BookableItem, variantOption: VariantOption, addOns: AddOn[], bundle: BundleData) => {
        const variant = variantOption.treatmentVariant;
        const duration = variant.steps?.[0]?.minuteEnd ?? 0;
        const itemCategoryNameDe = allSystemBookableItems.find(i => i.id === item.id)?.categoryNameDe ?? '';

        const addOnItems: SelectedAddOn[] = addOns.map((addon) => ({
            addOnId: addon.id,
            addOnNameDe: addon.nameDe,
            addOnNameEn: addon.nameEn,
            variantId: addon.treatmentVariant.id,
            variantName: addon.treatmentVariant.name,
            price: addon.treatmentVariant.priceCents / 100,
            durationMinutes: addon.treatmentVariant.steps?.[0]?.minuteEnd ?? 0,
        }));

        const newCartItem: CartItem = {
            bookableItemId: item.id,
            bookableItemNameDe: item.nameDe,
            bookableItemNameEn: item.nameEn,
            bookableItemDescriptionDe: item.descriptionDe,
            bookableItemDescriptionEn: item.descriptionEn,
            bookableItemType: BookingFunnelBookableItemType.BUNDLE,
            bookableItemImageUrl: item.images[0]?.url,
            selectedVariantId: variant.id,
            selectedVariantName: variant.name,
            selectedVariantOptionLabelDe: variantOption.optionLabelDe,
            selectedVariantOptionLabelEn: variantOption.optionLabelEn,
            price: Number(bundle.unitPriceGross) * bundle.unitQuantity,
            durationMinutes: duration,
            addOns: addOnItems,
            bundleId: bundle.id,
            bundleName: bundle.name,
            bundleUnitPriceGross: Number(bundle.unitPriceGross),
            bundleUnitQuantity: bundle.unitQuantity,
            categoryNameDe: itemCategoryNameDe,
        };

        setCartItems([...cartItems, newCartItem]);

        sendEvent(GTMEvent.BOOKING_FUNNEL_ADD_TO_CART, {
            items: [{
                product_id: variant.id,
                product_name: variant.name,
                category: itemCategoryNameDe,
                price_net: (Number(bundle.unitPriceGross) * bundle.unitQuantity) / 1.19,
                price_gross: Number(bundle.unitPriceGross) * bundle.unitQuantity,
                quantity: bundle.unitQuantity,
            }],
            branch: {
                id: selectedStore?.id,
                name: selectedStore?.name,
            },
            user: { id: null, has_account: false, email: null, phone: null },
        });

        setSelectedItem(null);
        setSelectedVariantOption(null);
        setSelectedAddOns([]);
        setAvailableBundles([]);
        setSelectedUpsellBundle(null);
        setPendingBundleAction(null);
        setIsBundleUpsellDrawerOpen(false);
    };

    const addToCartAsRedemption = (item: BookableItem, variantOption: VariantOption, addOns: AddOn[], bundle: BundleData) => {
        const variant = variantOption.treatmentVariant;
        const duration = variant.steps?.[0]?.minuteEnd ?? 0;
        const itemCategoryNameDe = allSystemBookableItems.find(i => i.id === item.id)?.categoryNameDe ?? '';

        const addOnItems: SelectedAddOn[] = addOns.map((addon) => ({
            addOnId: addon.id,
            addOnNameDe: addon.nameDe,
            addOnNameEn: addon.nameEn,
            variantId: addon.treatmentVariant.id,
            variantName: addon.treatmentVariant.name,
            price: addon.treatmentVariant.priceCents / 100,
            durationMinutes: addon.treatmentVariant.steps?.[0]?.minuteEnd ?? 0,
        }));

        const newCartItem: CartItem = {
            bookableItemId: item.id,
            bookableItemNameDe: item.nameDe,
            bookableItemNameEn: item.nameEn,
            bookableItemDescriptionDe: item.descriptionDe,
            bookableItemDescriptionEn: item.descriptionEn,
            bookableItemType: BookingFunnelBookableItemType.BUNDLE,
            bookableItemImageUrl: item.images[0]?.url,
            selectedVariantId: variant.id,
            selectedVariantName: variant.name,
            selectedVariantOptionLabelDe: variantOption.optionLabelDe,
            selectedVariantOptionLabelEn: variantOption.optionLabelEn,
            price: Number(bundle.unitPriceGross),
            durationMinutes: duration,
            addOns: addOnItems,
            bundleId: bundle.id,
            bundleName: bundle.name,
            bundleUnitPriceGross: Number(bundle.unitPriceGross),
            bundleUnitQuantity: bundle.unitQuantity,
            isBundleRedemption: true,
            categoryNameDe: itemCategoryNameDe,
        };

        setCartItems([...cartItems, newCartItem]);

        sendEvent(GTMEvent.BOOKING_FUNNEL_ADD_TO_CART, {
            items: [{
                product_id: variant.id,
                product_name: variant.name,
                category: itemCategoryNameDe,
                price_net: Number(bundle.unitPriceGross) / 1.19,
                price_gross: Number(bundle.unitPriceGross),
                quantity: 1,
            }],
            branch: {
                id: selectedStore?.id,
                name: selectedStore?.name,
            },
            user: { id: null, has_account: false, email: null, phone: null },
        });

        setSelectedItem(null);
        setSelectedVariantOption(null);
        setSelectedAddOns([]);
        setAvailableBundles([]);
        setSelectedUpsellBundle(null);
        setPendingBundleAction(null);
        setIsBundleUpsellDrawerOpen(false);
    };

    const handleSelectBundle = (bundle: BundleData) => {
        if (!selectedItem || !selectedVariantOption) return;
        sendEvent(GTMEvent.BOOKING_FUNNEL_BUNDLE_UPSELL_ACCEPTED, {
            bookable_item_id: selectedItem.id,
            bookable_item_name: selectedItem.nameDe,
            bundle_id: bundle.id,
            bundle_name: bundle.name,
            bundle_total_price: Number(bundle.unitPriceGross) * bundle.unitQuantity,
            oneoff_price_cents: selectedVariantOption.treatmentVariant.priceCents,
            gross_upsell_amount: (Number(bundle.unitPriceGross) * bundle.unitQuantity) - (selectedVariantOption.treatmentVariant.priceCents / 100),
        });
        setIsBundleUpsellDrawerOpen(false);
        if (selectedItem.addOns.length > 0) {
            setPendingBundleAction({ type: 'purchase', bundle });
            setIsAddOnDrawerOpen(true);
        } else {
            addToCartWithBundle(selectedItem, selectedVariantOption, [], bundle);
        }
    };

    const handleSkipBundle = () => {
        if (!selectedItem || !selectedVariantOption) return;
        sendEvent(GTMEvent.BOOKING_FUNNEL_BUNDLE_UPSELL_DECLINED, {
            bookable_item_id: selectedItem.id,
            bookable_item_name: selectedItem.nameDe,
            oneoff_price_cents: selectedVariantOption.treatmentVariant.priceCents,
        });
        setIsBundleUpsellDrawerOpen(false);
        setSelectedUpsellBundle(null);
        setPendingBundleAction(null);

        if (selectedItem.addOns.length > 0) {
            setIsAddOnDrawerOpen(true);
        } else {
            addToCart(selectedItem, selectedVariantOption, []);
        }
    };

    const handleRedeemBundle = (bundle: BundleData) => {
        if (!selectedItem || !selectedVariantOption) return;
        sendEvent(GTMEvent.BOOKING_FUNNEL_BUNDLE_UPSELL_REDEMPTION, {
            bookable_item_id: selectedItem.id,
            bookable_item_name: selectedItem.nameDe,
            bundle_id: bundle.id,
            bundle_name: bundle.name,
            oneoff_price_cents: selectedVariantOption.treatmentVariant.priceCents,
            redemption_price_gross: Number(bundle.unitPriceGross),
        });
        setIsBundleUpsellDrawerOpen(false);
        if (selectedItem.addOns.length > 0) {
            setPendingBundleAction({ type: 'redemption', bundle });
            setIsAddOnDrawerOpen(true);
        } else {
            addToCartAsRedemption(selectedItem, selectedVariantOption, [], bundle);
        }
    };

    const removeFromCart = (bookableItemId: string) => {
        setCartItems(cartItems.filter((item) => item.bookableItemId !== bookableItemId));
    };

    // Search header component - shows title OR search input
    const renderSearchHeader = () => {
        return (
            <div className="flex items-center gap-3">
                <div className="flex-1">
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                            const searchInput = e.target.value;
                            setSearchQuery(searchInput)
                            if (!searchInput.trim()) {
                                setIsSearchExpanded(false);
                            } else {
                                setIsSearchExpanded(true);
                            }
                        }}
                        onFocus={(e) => {
                            setIsSearchExpanded(true);
                        }}
                        onBlur={(e) => {
                            if (!searchQuery.trim()) {
                                setIsSearchExpanded(false);
                            }
                        }}
                        placeholder={config ? (language === Language.GERMAN ? config.searchPlaceholderDe : config.searchPlaceholderEn) : 'Search...'}
                        className="w-full bg-white rounded-lg px-4 py-2 text-burgundy placeholder:text-burgundy/40 outline-none border border-burgundy/20 focus:border-burgundy/50"
                    />
                </div>

                {isSearchExpanded ? (
                    <div
                        className="shrink-0 cursor-pointer"
                        onClick={handleCloseSearch}
                    >
                        <X className="w-5 h-5 text-burgundy hover:text-burgundy/70 transition-colors" />
                    </div>
                ) : (
                    <div
                        className="shrink-0 cursor-pointer"
                        onClick={handleOpenSearch}
                    >
                        <Search className="w-5 h-5 text-burgundy hover:text-burgundy/70 transition-colors" />
                    </div>
                )}
            </div>
        );
    };

    // Main view
    const renderMainView = () => {
        // If a category is selected, show its items
        if (selectedCategory) {
            const hasChildren = categoryHasChildren(selectedCategory) && selectedCategory.children.length > 0;
            const name = language === Language.GERMAN ? selectedCategory.nameDe : selectedCategory.nameEn;

            return (
                <>
                    {/* <div
                        className="text-sm text-burgundy/60 cursor-pointer hover:text-burgundy transition-colors px-1"
                        onClick={() => setSelectedCategoryId(null)}
                    >
                        ← {language === Language.GERMAN ? (config?.backButtonTextDe ?? 'Zurück') : (config?.backButtonTextEn ?? 'Back')}
                    </div> */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col gap-4"
                    >
                        {!selectedCategory.imageUrl && (
                            <h2 className="text-lg text-burgundy px-1">{name}</h2>
                        )}

                        {/* Child categories as accordions */}
                        {hasChildren && categoryHasChildren(selectedCategory) ? (
                            <div className="space-y-3">
                                {selectedCategory.children.map((child: CMSCategoryChild) => (
                                    <AccordionCategory
                                        key={child.id}
                                        category={child}
                                        onSelectItem={handleSelectItem}
                                        onMoreInfo={handleOpenInfoDrawer}
                                        cartItemIds={cartItemIds}
                                        moreInfoLinkText={config ? (language === Language.GERMAN ? config.moreInfoButtonTextDe : config.moreInfoButtonTextEn) : 'Mehr dazu'}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3">
                                {selectedCategory.bookableItems.map((item, index) => (
                                    <motion.div
                                        key={item.id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, delay: index * 0.03, ease: "easeOut" }}
                                    >
                                        <BookableItemCard
                                            item={item}
                                            onSelect={() => handleSelectItem(item)}
                                            onMoreInfo={() => handleOpenInfoDrawer(item)}
                                            isInCart={cartItemIds.includes(item.id)}
                                            showMoreInfoLink
                                            moreInfoLinkText={config ? (language === Language.GERMAN ? config.moreInfoButtonTextDe : config.moreInfoButtonTextEn) : 'Mehr dazu'}
                                        />
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </>
            );
        }

        // Main categories view
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col gap-8"
            >
                {/* Treatment Planner Card - shown when logged in with treatment plan */}
                <TreatmentPlannerCard
                    title={config ? (language === Language.GERMAN ? config.treatmentPlanTitleDe : config.treatmentPlanTitleEn) : undefined}
                    subtitle={config ? (language === Language.GERMAN ? config.treatmentPlanSubtitleDe : config.treatmentPlanSubtitleEn) : undefined}
                />

                {/* Recommendations */}
                {recommendedItems.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className="flex flex-col gap-4"
                    >
                        <h2 className="text-lg text-burgundy font-light px-1">
                            {config ? (language === Language.GERMAN ? config.recommendedSectionTitleDe : config.recommendedSectionTitleEn) : 'Recommendations'}
                        </h2>
                        <div className="grid grid-cols-3 gap-2 min-[401px]:gap-3">
                            {recommendedItems.slice(0, 3).map((item) => (
                                <BookableItemCard
                                    key={item.id}
                                    item={item}
                                    onSelect={() => handleOpenInfoDrawer(item)}
                                    isInCart={cartItemIds.includes(item.id)}
                                    isRecommended={true}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Categories / Search Results */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="flex flex-col gap-4"
                >
                    {renderSearchHeader()}

                    <AnimatePresence mode="wait">
                        {isSearchExpanded ? (
                            <motion.div
                                key="search-results"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.15 }}
                                className="flex flex-col gap-3"
                            >
                                <p className="text-sm text-burgundy/60 px-1">
                                    {searchQuery.trim()
                                        ? (language === Language.GERMAN ? 'Suchergebnisse' : 'Search Results')
                                        : (language === Language.GERMAN ? 'Alle Leistungen' : 'All Services')
                                    }
                                    <span className="ml-1">({searchFilteredItems.length})</span>
                                </p>
                                {searchFilteredItems.length === 0 ? (
                                    <p className="text-burgundy/50 px-1 py-8 text-center">
                                        {config ? (language === Language.GERMAN ? config.noResultsTextDe : config.noResultsTextEn) : 'No results'}
                                    </p>
                                ) : (
                                    <div className="grid grid-cols-1 gap-3">
                                        {searchFilteredItems.map((item, index) => (
                                            <motion.div
                                                key={item.id}
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.2, delay: index * 0.03, ease: "easeOut" }}
                                            >
                                                <BookableItemCard
                                                    item={item}
                                                    onSelect={() => handleSelectItem(item)}
                                                    onMoreInfo={() => handleOpenInfoDrawer(item)}
                                                    isInCart={cartItemIds.includes(item.id)}
                                                    showMoreInfoLink
                                                    moreInfoLinkText={config ? (language === Language.GERMAN ? config.moreInfoButtonTextDe : config.moreInfoButtonTextEn) : 'Mehr dazu'}
                                                />
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        ) : (
                            <motion.div
                                key="categories"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.15 }}
                            >
                                {isLoading ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {Array.from({ length: 6 }).map((_, idx) => (
                                            <div key={idx} className="h-[90px] rounded-[5px] bg-burgundy/5 animate-pulse" />
                                        ))}
                                    </div>
                                ) : currentCategories.length === 0 ? (
                                    <p className="text-burgundy/50 text-center py-8">
                                        {language === Language.GERMAN ? (config?.noCategoriesMessageDe ?? 'Keine Kategorien verfügbar') : (config?.noCategoriesMessageEn ?? 'No categories available')}
                                    </p>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {currentCategories.map((category) => (
                                            <CategoryCard
                                                key={category.id}
                                                category={category}
                                                onClick={() => setSelectedCategoryId(category.id)}
                                                hasChildren={category.children.length > 0 || category.bookableItems.length > 0}
                                            />
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        );
    };

    const progressText = config
        ? (language === Language.GERMAN ? config.progressDisplayDe : config.progressDisplayEn)
        : 'SCHRITT 3 / 5';

    return (
        <>
            <FixedBottomBar />

            <motion.div
                className="flex flex-col min-h-screen"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={60} displayString={progressText} onClickBackButton={handleBack} />

                {selectedCategory?.imageUrl && (
                    <motion.div
                        key={selectedCategory.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="relative w-full h-34 md:h-48 lg:h-60 shrink-0 overflow-hidden"
                    >
                        <img
                            src={selectedCategory.imageUrl}
                            alt=""
                            aria-hidden
                            className="hidden lg:block absolute inset-0 w-full h-full object-cover blur-2xl scale-110"
                        />
                        <img
                            src={selectedCategory.imageUrl}
                            alt={language === Language.GERMAN ? selectedCategory.nameDe : selectedCategory.nameEn}
                            className="absolute inset-0 w-full h-full object-cover lg:object-contain"
                        />
                        <div className="absolute inset-0 bg-linear-to-t from-black/50 via-black/10 to-transparent" />
                        <h2 className="absolute bottom-4 left-6 right-6 text-xl font-medium text-white">
                            {language === Language.GERMAN ? selectedCategory.nameDe : selectedCategory.nameEn}
                        </h2>
                    </motion.div>
                )}

                {/* Content */}
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-6 pt-8 pb-48">
                    <div className="flex flex-col gap-8">
                        {renderMainView()}
                    </div>
                </div>
            </motion.div>

            {/* Info Drawer */}
            <Sheet open={isInfoDrawerOpen} onOpenChange={(open) => {
                setIsInfoDrawerOpen(open);
                if (!open) setInfoDrawerMode('info'); // Reset mode when closing
            }}>
                <SheetContent side="bottom" className={`bg-white border-0! flex flex-col p-0 rounded-t-2xl overflow-hidden ${selectedItem?.images[0]?.url ? 'h-[75vh] md:h-[70vh]' : 'h-[60vh] md:h-[60vh]'}`}>
                    {selectedItem && (() => {
                        const isBundle = selectedItem.type === BookingFunnelBookableItemType.BUNDLE && selectedItem.bundle;
                        const bundle = selectedItem.bundle;

                        // For bundles, use bundle data; for services, use variant data
                        const firstVariant = isBundle && bundle
                            ? bundle.treatmentVariant
                            : selectedItem.variantOptions[0]?.treatmentVariant;
                        const cheapestBundlePrice = !isBundle && selectedItem.availableBundles.length > 0
                            ? Math.min(...selectedItem.availableBundles.map(b => Number(b.unitPriceGross)))
                            : null;
                        const lowestPrice = isBundle && bundle
                            ? bundle.unitPriceGross
                            : cheapestBundlePrice ?? (firstVariant ? firstVariant.priceCents / 100 : 0);
                        const duration = firstVariant?.steps?.[0]?.minuteEnd ?? 0;
                        const hasMultipleVariants = !isBundle && selectedItem.variantOptions.length > 1;

                        // Bundle-specific calculations
                        const bundleQuantity = bundle?.unitQuantity ?? 1;
                        const bundleTotalPrice = isBundle && bundle ? bundle.unitPriceGross * bundleQuantity : 0;
                        const regularTotalPrice = isBundle && bundle ? (bundle.treatmentVariant.priceCents / 100) * bundleQuantity : 0;
                        const savings = regularTotalPrice - bundleTotalPrice;
                        const savingsPercent = regularTotalPrice > 0 ? Math.round((savings / regularTotalPrice) * 100) : 0;

                        const pricePrefix = config
                            ? (language === Language.GERMAN ? config.pricePrefixTextDe : config.pricePrefixTextEn)
                            : 'ab';
                        const selectButtonText = config
                            ? (language === Language.GERMAN ? config.selectButtonTextDe : config.selectButtonTextEn)
                            : 'AUSWÄHLEN';
                        const chooseOptionTitle = config
                            ? (language === Language.GERMAN ? config.chooseOptionTitleDe : config.chooseOptionTitleEn)
                            : 'Wähle eine Option';

                        return (
                            <div className="flex flex-col h-full">
                                {/* Hero image - only shown if bookable item has an image */}
                                {selectedItem.images[0]?.url && (
                                    <div className="w-full h-[20vh] shrink-0 relative">
                                        <img
                                            src={selectedItem.images[0].url}
                                            alt={language === Language.GERMAN ? selectedItem.nameDe : selectedItem.nameEn}
                                            className="w-full h-full object-cover"
                                        />
                                        {/* Bundle badge overlay */}
                                        {isBundle && (
                                            <div className="absolute top-3 right-3 bg-burgundy text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5">
                                                <Gift className="w-4 h-4" />
                                                {language === Language.GERMAN ? 'Bundle' : 'Bundle'}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Header - fixed at top */}
                                <SheetHeader className="md:max-w-3xl w-full md:mx-auto text-left pt-6 pb-4 px-6 md:px-0 shrink-0">
                                    {/* Bundle badge if no image */}
                                    {isBundle && !selectedItem.images[0]?.url && (
                                        <div className="flex items-center gap-1.5 text-burgundy mb-2">
                                            <Gift className="w-4 h-4" />
                                            <span className="text-sm font-medium">{language === Language.GERMAN ? 'Bundle-Angebot' : 'Bundle Offer'}</span>
                                        </div>
                                    )}
                                    <SheetTitle className="text-burgundy text-xl">
                                        {language === Language.GERMAN ? selectedItem.nameDe : selectedItem.nameEn}
                                    </SheetTitle>
                                    {/* Price and Duration Row - different display for bundles */}
                                    {isBundle && bundle ? (
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-burgundy font-semibold">
                                                    {bundleQuantity}x {formatBundleUnitPrice(bundle.unitPriceGross)} EUR
                                                </span>
                                                <span className="text-burgundy/30">|</span>
                                                <span className="text-burgundy/70">{duration} MIN {language === Language.GERMAN ? 'pro Sitzung' : 'per session'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-burgundy/50 line-through">
                                                    {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(regularTotalPrice)} EUR
                                                </span>
                                                <span className="text-burgundy font-semibold">
                                                    {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(bundleTotalPrice)} EUR
                                                </span>
                                                {savings > 0 && (
                                                    <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                                                        -{savingsPercent}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 text-sm text-burgundy/70">
                                            <span>
                                                {pricePrefix}{' '}
                                                {cheapestBundlePrice !== null
                                                    ? formatBundleUnitPrice(cheapestBundlePrice)
                                                    : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(lowestPrice)} EUR
                                            </span>
                                            <span className="text-burgundy/30">|</span>
                                            <span>{duration} MIN</span>
                                        </div>
                                    )}
                                </SheetHeader>

                                {/* Scrollable content area */}
                                <div className="flex-1 overflow-y-auto px-6">
                                    <div className="md:max-w-3xl md:mx-auto space-y-6 pb-4">
                                        {/* Bundle Deal Highlight - only for bundles */}
                                        {isBundle && bundle && (
                                            <div className="bg-linear-to-r from-burgundy/5 to-burgundy/10 border border-burgundy/20 rounded-xl p-4 space-y-3">
                                                <h3 className="text-sm font-medium text-burgundy flex items-center gap-2">
                                                    <Gift className="w-4 h-4" />
                                                    {language === Language.GERMAN ? 'Bundle-Details' : 'Bundle Details'}
                                                </h3>
                                                <div className="space-y-2">
                                                    <div className="flex justify-between items-center text-sm">
                                                        <span className="text-burgundy/70">{language === Language.GERMAN ? 'Behandlung' : 'Treatment'}</span>
                                                        <span className="text-burgundy font-medium">{bundle.treatmentVariant.name}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center text-sm">
                                                        <span className="text-burgundy/70">{language === Language.GERMAN ? 'Anzahl Sitzungen' : 'Number of Sessions'}</span>
                                                        <span className="text-burgundy font-medium">{bundleQuantity}x</span>
                                                    </div>
                                                    <div className="flex justify-between items-center text-sm">
                                                        <span className="text-burgundy/70">{language === Language.GERMAN ? 'Preis pro Sitzung' : 'Price per Session'}</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-burgundy/50 line-through text-xs">
                                                                {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(bundle.treatmentVariant.priceCents / 100)} EUR
                                                            </span>
                                                            <span className="text-burgundy font-medium">
                                                                {formatBundleUnitPrice(bundle.unitPriceGross)} EUR
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="border-t border-burgundy/10 pt-2 mt-2">
                                                        <div className="flex justify-between items-center text-sm">
                                                            <span className="text-burgundy font-medium">{language === Language.GERMAN ? 'Gesamtpreis' : 'Total Price'}</span>
                                                            <span className="text-burgundy font-semibold text-base">
                                                                {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(bundleTotalPrice)} EUR
                                                            </span>
                                                        </div>
                                                        {savings > 0 && (
                                                            <div className="flex justify-between items-center text-sm mt-1">
                                                                <span className="text-green-600">{language === Language.GERMAN ? 'Du sparst' : 'You save'}</span>
                                                                <span className="text-green-600 font-medium">
                                                                    {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(savings)} EUR
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Description Section */}
                                        {(language === Language.GERMAN ? selectedItem.descriptionDe : selectedItem.descriptionEn) && (
                                            <div className="space-y-2">
                                                <h3 className="text-sm font-light text-primary-400">
                                                    {config ? (language === Language.GERMAN ? config.descriptionSectionTitleDe : config.descriptionSectionTitleEn) : 'Beschreibung'}
                                                </h3>
                                                <p className="text-sm text-burgundy/70 leading-relaxed">
                                                    {language === Language.GERMAN ? selectedItem.descriptionDe : selectedItem.descriptionEn}
                                                </p>
                                            </div>
                                        )}

                                        {/* Key Benefits */}
                                        {((language === Language.GERMAN ? selectedItem.keyBenefitsDe : selectedItem.keyBenefitsEn) || []).length > 0 && (
                                            <div className="space-y-2">
                                                <h3 className="text-sm font-light text-primary-400">
                                                    {config ? (language === Language.GERMAN ? config.benefitsSectionTitleDe : config.benefitsSectionTitleEn) : 'Benefits'}
                                                </h3>
                                                <div className="flex flex-wrap gap-2">
                                                    {(language === Language.GERMAN ? selectedItem.keyBenefitsDe : selectedItem.keyBenefitsEn).map((benefit, idx) => (
                                                        <Badge key={idx} className="bg-burgundy/10 text-burgundy text-xs px-2 py-0.5 font-normal">
                                                            {benefit}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Customer Review / You Should Know */}
                                        {(language === Language.GERMAN ? selectedItem.youShouldKnowThisDe : selectedItem.youShouldKnowThisEn) && (
                                            <div className="space-y-2">
                                                <h3 className="text-sm font-light text-primary-400">
                                                    {config ? (language === Language.GERMAN ? config.customerReviewTitleDe : config.customerReviewTitleEn) : 'Kunden Rezension'}
                                                </h3>
                                                <p className="text-sm text-burgundy/70 leading-relaxed">
                                                    {language === Language.GERMAN ? selectedItem.youShouldKnowThisDe : selectedItem.youShouldKnowThisEn}
                                                </p>
                                            </div>
                                        )}

                                        {/* Variant Options - only shown when in 'options' mode for SERVICE items */}
                                        {!isBundle && infoDrawerMode === 'options' && hasMultipleVariants && (
                                            <div className="space-y-3">
                                                <h3 className="text-sm font-light text-primary-400">
                                                    {chooseOptionTitle}
                                                </h3>
                                                <div className="space-y-2">
                                                    {selectedItem.variantOptions.map((option) => {
                                                        const variant = option.treatmentVariant;
                                                        const variantDuration = variant.steps?.[0]?.minuteEnd ?? 0;
                                                        const label = language === Language.GERMAN ? option.optionLabelDe : option.optionLabelEn;

                                                        return (
                                                            <div
                                                                key={option.id}
                                                                className="flex items-center justify-between p-3 border border-burgundy/20 rounded-lg cursor-pointer hover:border-burgundy/40 hover:bg-burgundy/5 transition-all"
                                                                onClick={() => handleSelectVariant(option)}
                                                            >
                                                                <div>
                                                                    <p className="font-medium text-burgundy">{label || variant.name}</p>
                                                                    <p className="text-xs text-burgundy/50">{variantDuration} MIN</p>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-semibold text-burgundy">
                                                                        {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(variant.priceCents / 100)} EUR
                                                                    </span>
                                                                    <ChevronRight className="w-4 h-4 text-burgundy/50" />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Footer - always fixed at bottom */}
                                {(infoDrawerMode === 'info' || !hasMultipleVariants || isBundle) && (
                                    <SheetFooter className="md:max-w-3xl w-full md:mx-auto px-6 py-4 border-t border-burgundy/10 bg-white shrink-0">
                                        <PrimaryActionButton
                                            onClick={handleInfoDrawerSelect}
                                            label={selectButtonText.toUpperCase()}
                                            rightIcon={<ArrowRight />}
                                        />
                                    </SheetFooter>
                                )}
                            </div>
                        );
                    })()}
                </SheetContent>
            </Sheet>

            {/* Add-On Selection Drawer */}
            <Sheet open={isAddOnDrawerOpen} onOpenChange={setIsAddOnDrawerOpen}>
                <SheetContent side="bottom" className="bg-white max-h-[90vh] overflow-y-auto rounded-t-[20px]">
                    {selectedItem && (
                        <>
                            <SheetHeader className="md:max-w-3xl md:mx-auto text-center pt-6">
                                <SheetTitle className="text-burgundy text-xl font-semibold">
                                    {config ? (language === Language.GERMAN ? config.addOnPopupTitleDe : config.addOnPopupTitleEn) : 'Add-Ons'}
                                </SheetTitle>
                                <SheetDescription className="text-burgundy/70 text-sm">
                                    {config ? (language === Language.GERMAN ? config.addOnPopupDescriptionDe : config.addOnPopupDescriptionEn) : ''}
                                </SheetDescription>
                            </SheetHeader>

                            <div className="md:max-w-3xl w-full md:mx-auto px-4 py-5 space-y-3">
                                {selectedItem.addOns.map((addOn) => {
                                    const isSelected = selectedAddOns.some((a) => a.id === addOn.id);
                                    const variant = addOn.treatmentVariant;
                                    const duration = variant.steps?.[0]?.minuteEnd ?? 0;

                                    return (
                                        <div
                                            key={addOn.id}
                                            className={`w-full flex items-center justify-between p-4 md:p-5 border rounded-md cursor-pointer transition-all shadow-sm ${isSelected
                                                ? 'border-burgundy/30 bg-linear-to-br from-white to-[#FBEEF0]'
                                                : 'border-burgundy/20 bg-linear-to-br from-white to-[#FBEEF0]/50 hover:border-burgundy/40'
                                                }`}
                                            style={{ boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' }}
                                            onClick={() => toggleAddOn(addOn)}
                                        >
                                            <div className="flex items-center gap-3 md:gap-4">
                                                {addOn.imageUrl && (
                                                    <img src={addOn.imageUrl} alt="" className="w-12 h-12 md:w-14 md:h-14 object-cover rounded" />
                                                )}
                                                <div>
                                                    <p className="font-semibold text-burgundy text-sm md:text-base">
                                                        {language === Language.GERMAN ? addOn.nameDe : addOn.nameEn}
                                                    </p>
                                                    <p className="text-xs md:text-sm text-burgundy/60">
                                                        +{duration} MIN | <span className="text-[#FF6439] font-medium">+{new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(variant.priceCents / 100)} EUR</span>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className={`ml-2 md:ml-4 w-6 h-6 md:w-7 md:h-7 rounded-sm border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-burgundy bg-burgundy' : 'border-burgundy/30'
                                                }`}>
                                                {isSelected && (
                                                    <Check className="w-3 h-3 md:w-4 md:h-4 text-white" strokeWidth={3} />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <SheetFooter className="md:max-w-3xl w-full md:mx-auto px-4 pb-8 flex flex-col gap-3">
                                <PrimaryActionButton
                                    onClick={confirmAddOns}
                                    label={selectedAddOns.length > 0
                                        ? (config ? (language === Language.GERMAN ? config.addToCartButtonTextDe : config.addToCartButtonTextEn) : 'Add to cart').toUpperCase()
                                        : (language === Language.GERMAN ? 'Weiter ohne Zusatzleistung' : 'Continue without add-on').toUpperCase()
                                    }
                                    rightIcon={<ArrowRight />}
                                    variant={selectedAddOns.length > 0 ? 'filled' : 'outlined'}
                                />
                            </SheetFooter>
                        </>
                    )}
                </SheetContent>
            </Sheet>

            {/* Bundle Upsell Drawer */}
            <Sheet open={isBundleUpsellDrawerOpen} onOpenChange={setIsBundleUpsellDrawerOpen}>
                <SheetContent side="bottom" className="bg-white max-h-[90vh] overflow-y-auto rounded-t-[20px]">
                    {selectedItem && selectedVariantOption && availableBundles.length > 0 && (() => {
                        const singleTreatmentPrice = selectedVariantOption.treatmentVariant.priceCents / 100;
                        const hasMultipleBundles = availableBundles.length > 1;
                        const fmtEuro = (value: number) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
                        const fmtSavings = (value: number) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(value);

                        // CMS texts with Figma fallbacks (used when the field is empty or config is missing)
                        const upsellTitle = (config && (language === Language.GERMAN ? config.bundleUpsellTitleDe : config.bundleUpsellTitleEn))
                            || (language === Language.GERMAN ? 'Bundle auswählen' : 'Select bundle');
                        const upsellSubtitle = (config && (language === Language.GERMAN ? config.bundleUpsellSubtitleDe : config.bundleUpsellSubtitleEn))
                            || (language === Language.GERMAN ? 'Neues Bundle buchen oder bestehendes einlösen.' : 'Buy a new bundle or redeem an existing one.');
                        const declineButtonText = (config && (language === Language.GERMAN ? config.bundleUpsellDeclineButtonTextDe : config.bundleUpsellDeclineButtonTextEn))
                            || (language === Language.GERMAN ? 'Einzelleistung buchen' : 'Book single treatment');
                        const acceptButtonText = (config && (language === Language.GERMAN ? config.bundleUpsellAcceptButtonTextDe : config.bundleUpsellAcceptButtonTextEn))
                            || (language === Language.GERMAN ? 'Neues Bundle buchen' : 'Buy new bundle');
                        const redemptionTitle = (config && (language === Language.GERMAN ? config.bundleUpsellRedemptionTitleDe : config.bundleUpsellRedemptionTitleEn))
                            || (language === Language.GERMAN ? 'Bestehendes Bundle einlösen' : 'Redeem existing bundle');
                        const redemptionText = (config && (language === Language.GERMAN ? config.bundleUpsellRedemptionTextDe : config.bundleUpsellRedemptionTextEn))
                            || (language === Language.GERMAN ? 'Deine nächste Behandlung' : 'Your next treatment');
                        const redemptionButtonText = (config && (language === Language.GERMAN ? config.bundleUpsellRedemptionButtonTextDe : config.bundleUpsellRedemptionButtonTextEn))
                            || (language === Language.GERMAN ? 'Jetzt einlösen' : 'Redeem now');

                        return (
                            <>
                                <SheetHeader className="md:max-w-3xl md:mx-auto text-center pt-6 px-4">
                                    <SheetTitle className="text-burgundy text-xl font-semibold">
                                        {upsellTitle}
                                    </SheetTitle>
                                    <SheetDescription className="text-burgundy text-xs">
                                        {upsellSubtitle}
                                    </SheetDescription>
                                </SheetHeader>

                                <div className="md:max-w-3xl w-full md:mx-auto px-4 space-y-3 pt-0">
                                    {/* Redemption card */}
                                    <div className="relative bg-white border border-burgundy/30 shadow-[0px_2px_4px_rgba(0,0,0,0.2)] rounded-[5px] p-4 flex flex-col gap-2">
                                        <p className="font-semibold text-burgundy text-sm">{redemptionTitle}</p>
                                        <p className="text-burgundy text-[10px] leading-tight">{redemptionText}</p>
                                        <button
                                            onClick={() => {
                                                const bundleToRedeem = hasMultipleBundles ? selectedUpsellBundle : availableBundles[0];
                                                if (bundleToRedeem) { handleRedeemBundle(bundleToRedeem); }
                                            }}
                                            disabled={hasMultipleBundles && !selectedUpsellBundle}
                                            className={`mt-1 w-full py-[11px] px-4 rounded-full border text-xs font-medium transition-colors ${
                                                hasMultipleBundles && !selectedUpsellBundle
                                                    ? 'border-burgundy/40 text-burgundy/40 cursor-not-allowed'
                                                    : 'border-burgundy text-burgundy bg-white hover:bg-burgundy/5'
                                            }`}
                                        >
                                            {redemptionButtonText}
                                        </button>
                                    </div>

                                    {/* Bundle purchase card(s) */}
                                    {availableBundles.map((bundle) => {
                                        const bundleTotalPrice = Number(bundle.unitPriceGross) * bundle.unitQuantity;
                                        const perTreatment = Number(bundle.unitPriceGross);
                                        const savings = (singleTreatmentPrice - perTreatment) * bundle.unitQuantity;
                                        const isSelected = !hasMultipleBundles || selectedUpsellBundle?.id === bundle.id;

                                        return (
                                            <div
                                                key={bundle.id}
                                                onClick={() => { if (hasMultipleBundles) { setSelectedUpsellBundle((prev) => (prev?.id === bundle.id ? null : bundle)); } }}
                                                className={`relative rounded-[5px] p-4 shadow-[0px_2px_4px_rgba(0,0,0,0.2)] bg-[linear-gradient(46.35deg,#FFFFFF_4.89%,#FBEEF0_86.02%)] transition-all ${
                                                    hasMultipleBundles
                                                        ? isSelected
                                                            ? 'border-2 border-primary-400 cursor-pointer'
                                                            : 'border-2 border-burgundy/20 cursor-pointer hover:border-burgundy/40'
                                                        : 'border border-burgundy/20'
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="font-semibold text-burgundy text-sm">
                                                        {language === Language.GERMAN ? `Neues ${bundle.unitQuantity}er Package buchen` : `Buy new ${bundle.unitQuantity}-pack Bundle`}
                                                    </p>
                                                    <div className="flex items-center gap-2">
                                                        {savings > 0 && (
                                                            <span className="shrink-0 bg-[#20A26C]/30 text-[#188658] text-[10px] rounded-full px-2.5 py-1">
                                                                {language === Language.GERMAN ? `Spare ${fmtSavings(savings)} € im Paket` : `Save €${fmtSavings(savings)} with a Bundle`}
                                                            </span>
                                                        )}
                                                        {hasMultipleBundles && (
                                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                                                                isSelected ? 'border-primary-400 bg-primary-400' : 'border-burgundy/30'
                                                            }`}>
                                                                {isSelected && <Check className="w-3 h-3 text-white" />}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <p className="mt-3 font-semibold text-primary-400 text-xs">
                                                    {language === Language.GERMAN ? 'Pro Behandlung:' : 'Per Treatment:'}
                                                </p>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="font-semibold text-primary-400 text-xl">{fmtEuro(perTreatment)} €</span>
                                                    <span className="text-[#99747E] text-xs line-through">
                                                        {fmtEuro(singleTreatmentPrice)} € / {language === Language.GERMAN ? 'Behandlung' : 'Treatment'}
                                                    </span>
                                                </div>
                                                <p className="mt-1 text-[#99747E] text-[10px]">
                                                    {fmtSavings(bundleTotalPrice)} € {language === Language.GERMAN ? 'Gesamt' : 'Total'} · {bundle.unitQuantity} {language === Language.GERMAN ? 'Behandlungen' : 'Treatments'}
                                                </p>

                                                {!hasMultipleBundles && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleSelectBundle(bundle); }}
                                                        className="mt-4 w-full py-[11px] px-4 rounded-full bg-primary-400 text-white text-sm font-medium hover:bg-primary-500 transition-colors"
                                                    >
                                                        {acceptButtonText}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {/* Single shared buy button for multiple bundles */}
                                    {hasMultipleBundles && (
                                        <button
                                            onClick={() => { if (selectedUpsellBundle) { handleSelectBundle(selectedUpsellBundle); } }}
                                            disabled={!selectedUpsellBundle}
                                            className={`w-full py-[11px] px-4 rounded-full text-sm font-medium transition-colors ${
                                                selectedUpsellBundle
                                                    ? 'bg-primary-400 text-white hover:bg-primary-500'
                                                    : 'bg-primary-400/40 text-white/70 cursor-not-allowed'
                                            }`}
                                        >
                                            {acceptButtonText}
                                        </button>
                                    )}
                                </div>

                                {/* Decline - book single treatment */}
                                <SheetFooter className="md:max-w-3xl w-full md:mx-auto px-4 pb-8 pt-2">
                                    <button
                                        onClick={handleSkipBundle}
                                        className="w-full text-center text-xs font-medium underline text-burgundy hover:text-burgundy/70 transition-colors"
                                    >
                                        {declineButtonText} ({language === Language.GERMAN ? 'ab' : 'from'} {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(singleTreatmentPrice)} EUR)
                                    </button>
                                </SheetFooter>
                            </>
                        );
                    })()}
                </SheetContent>
            </Sheet>
        </>
    );
};

export default ServiceSelection;
